import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { MailCheck, SendHorizontal, UserPlus2 } from 'lucide-react';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import {
  SectionCard,
  Field,
  TextInput,
  TextAreaInput,
  ErrorState,
} from './SuperAdminUI';

const INITIAL_STATE = {
  name: '',
  email: '',
  shop_name: '',
  trial_days: 10,
  credits: 100,
  notes: '',
};

const SuperAdminProvisionForm = () => {
  const { provisionAdmin } = useSuperadminAuth();
  const [form, setForm] = useState(INITIAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const canSubmit = useMemo(() => {
    return (
      String(form.name).trim() &&
      String(form.email).trim() &&
      String(form.shop_name).trim() &&
      Number(form.trial_days) >= 0 &&
      Number(form.credits) >= 0
    );
  }, [form]);

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(null);

    try {
      const payload = {
        ...form,
        trial_days: Number(form.trial_days),
        credits: Number(form.credits),
      };

      const response = await provisionAdmin(payload);
      if (!response.ok) {
        throw new Error(response.error || 'Provisioning failed');
      }

      setSuccess(response.data);
      setForm(INITIAL_STATE);
      toast.success('Admin and shop provisioned successfully.');
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.error || err.message || 'Provisioning failed';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <SectionCard
      title="Provision Admin"
      subtitle="Create a new shop admin, issue trial credits, and send the token-based onboarding email."
      icon={<UserPlus2 className="h-5 w-5" style={{ color: '#7d6633' }} />}
    >
      <div className="space-y-5">
        {error ? <ErrorState error={error} /> : null}
        {success ? (
          <div className="rounded-[28px] border border-emerald-200 bg-emerald-50/70 p-5 text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100">
            <div className="flex items-start gap-3">
              <div className="rounded-2xl bg-emerald-500/10 p-3 text-emerald-600 dark:text-emerald-300">
                <MailCheck className="h-5 w-5" />
              </div>
              <div className="space-y-2">
                <p className="text-lg font-semibold">Provisioning complete</p>
                <p className="text-sm opacity-90">
                  User #{success.user_id} and shop #{success.shop_id} were created successfully.
                </p>
                <p className="text-sm font-medium">
                  Email sent: {success.email_sent ? 'Yes, the onboarding email was delivered.' : 'No confirmation returned.'}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <form className="space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-5 lg:grid-cols-2">
            <Field label="Admin name">
              <TextInput value={form.name} onChange={(event) => updateField('name', event.target.value)} placeholder="John Doe" />
            </Field>
            <Field label="Admin email">
              <TextInput
                type="email"
                value={form.email}
                onChange={(event) => updateField('email', event.target.value)}
                placeholder="john@example.com"
              />
            </Field>
            <Field label="Shop name">
              <TextInput value={form.shop_name} onChange={(event) => updateField('shop_name', event.target.value)} placeholder="John Barber Shop" />
            </Field>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Trial days">
                <TextInput
                  type="number"
                  min="0"
                  value={form.trial_days}
                  onChange={(event) => updateField('trial_days', event.target.value)}
                />
              </Field>
              <Field label="Credits">
                <TextInput
                  type="number"
                  min="0"
                  value={form.credits}
                  onChange={(event) => updateField('credits', event.target.value)}
                />
              </Field>
            </div>
          </div>

          <Field label="Notes">
            <TextAreaInput
              rows="4"
              value={form.notes}
              onChange={(event) => updateField('notes', event.target.value)}
              placeholder="free test account"
            />
          </Field>

          <div className="flex flex-col gap-3 rounded-[28px] border border-white/10 bg-white/50 p-4 dark:bg-zinc-950/35 md:flex-row md:items-center md:justify-between">
            <div className="text-sm text-zinc-600 dark:text-zinc-400">
              The backend handles token generation, onboarding delivery, and additive credit grants. No Stripe onboarding behavior is changed.
            </div>
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className="inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition disabled:cursor-not-allowed disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #7d6633 0%, #c9a038 100%)' }}
            >
              <SendHorizontal className="h-4 w-4" />
              {submitting ? 'Provisioning...' : 'Provision admin'}
            </button>
          </div>
        </form>
      </div>
    </SectionCard>
  );
};

export default SuperAdminProvisionForm;
